// Der Service-Worker aus Sicht der Seite: anmelden, und beim Abmelden alles
// wegräumen, was er und der Entwurfsspeicher über diese Person wissen.
//
// Der Worker selbst ist app/views/pwa/service-worker.js; ob er überhaupt
// angemeldet wird, entscheidet der Server über `<meta name="service-worker">`
// (nie in development -- Vites HMR und ein Worker, der Navigationen aus dem
// Cache beantwortet, kommen sich in die Quere). Alles hier ist "best effort":
// ein Browser ohne Service-Worker, ein Registrierungsfehler, ein fehlender
// Controller -- nichts davon darf die Seite stören, die ohne Offline-Fähigkeit
// genau so gut funktioniert wie vorher.

import { DRAFT_DATABASE } from '../editor/drafts'
import { PENDING_LAUNCH_DATABASE } from './pendingLaunch'
import { clearPushSubscription, type RegistrationLike } from './push'
import { showUpdateBanner } from '../ui/updateBanner'

export const SERVICE_WORKER_META = 'service-worker'

// Devises Abmelde-Formular (`button_to "Abmelden", destroy_user_session_path`).
export const SIGN_OUT_PATH = '/users/sign_out'

// Die zuletzt gesehene Konto-Id (localStorage). Das Abmelde-Formular deckt
// nur den einen Weg ab; eine Sitzung kann auch anderswo enden (Ablauf,
// anderer Tab), und dann meldet sich womöglich ein *anderes* Konto an. Dessen
// Seite darf weder die gecachten Seiten noch die Entwürfe der Person davor
// adoptieren — der Merker macht den Wechsel erkennbar.
export const ACCOUNT_STORAGE_KEY = 'schnittklar-account'
export const ACCOUNT_META = 'current-user-id'

type Controller = { postMessage(message: unknown): void }

// Der Ausschnitt eines ServiceWorker, den die Update-Logik braucht --
// Attrappen in serviceWorker.test.ts spielen ihn nach.
export type WorkerLike = {
  state: string
  postMessage(message: unknown): void
  addEventListener(type: 'statechange', listener: () => void): void
}

export type UpdatableRegistration = {
  waiting: WorkerLike | null
  installing: WorkerLike | null
  addEventListener(type: 'updatefound', listener: () => void): void
}

export type ServiceWorkerContainer = {
  register(url: string): Promise<unknown>
  controller: Controller | null
  // Optional aus demselben Grund wie `getRegistration` unten: die Attrappen
  // der älteren Tests kennen es nicht. `applyUpdate` hört darauf.
  addEventListener?(type: 'controllerchange', listener: () => void, options?: { once: boolean }): void
  // Optional, nicht weil der echte `navigator.serviceWorker` es je vermissen
  // ließe, sondern damit die bestehenden Test-Attrappen ohne diese Methode
  // (unten, für `clearOfflineData`/`registerServiceWorker`) weiter kompilieren.
  // Wird an `clearPushSubscription` (lib/push.ts) durchgereicht.
  getRegistration?(): Promise<RegistrationLike | undefined>
}

export type CacheStorageLike = {
  keys(): Promise<string[]>
  delete(key: string): Promise<boolean>
}

export type IndexedDbLike = {
  deleteDatabase(name: string): { onsuccess: unknown; onerror: unknown; onblocked: unknown }
}

// Wie viele Projektseiten das Dashboard dem Worker zum Vorwärmen nennt --
// dieselbe Zahl wie eine Dashboard-Seite (24 Karten) und wie WARM_LIMIT im
// Worker selbst (app/views/pwa/service-worker.js).
export const WARM_URL_LIMIT = 24

/**
 * Nennt dem Worker die Seiten der gelisteten Projekte, damit offline jedes
 * davon aufgeht -- nicht nur die zuletzt besuchten
 * (docs/superpowers/specs/2026-09-06-pwa-offline-round.md). Wahr, wenn die
 * Nachricht rausging; falsch ohne Controller oder ohne Kennungen -- beides
 * ist kein Fehler, nur ein Gerät ohne Offline-Fähigkeit.
 */
export function warmProjectPages(container: ServiceWorkerContainer | undefined, projectIds: number[]): boolean {
  const controller = container?.controller ?? null
  if (controller === null || projectIds.length === 0) return false

  const urls = [...new Set(projectIds)].slice(0, WARM_URL_LIMIT).map((id) => `/projects/${id}`)
  controller.postMessage({ type: 'warm', urls })
  return true
}

/** Der Pfad aus dem Meta-Tag, oder null, wenn der Server keinen genannt hat. */
export function serviceWorkerPath(metaContent: string | null | undefined): string | null {
  const path = metaContent?.trim() ?? ''
  return path.startsWith('/') ? path : null
}

/** Ob dieses Formular die Abmeldung ist. */
export function isSignOutForm(form: { getAttribute(name: string): string | null }): boolean {
  const action = form.getAttribute('action') ?? ''
  return action === SIGN_OUT_PATH || action.endsWith(SIGN_OUT_PATH)
}

/**
 * Meldet den Worker an. Wahr, wenn die Registrierung angestoßen wurde und
 * durchging; falsch für jeden Grund, aus dem sie es nicht tat. `onRegistered`
 * bekommt die Registrierung, wenn es eine gibt (für `watchForUpdate`).
 */
export async function registerServiceWorker(
  path: string | null,
  container: ServiceWorkerContainer | undefined,
  onRegistered: (registration: unknown) => void = () => undefined,
): Promise<boolean> {
  if (path === null || container === undefined) return false

  try {
    onRegistered(await container.register(path))
    return true
  } catch (error) {
    console.warn('Service worker did not register', error)
    return false
  }
}

// Ob das, was `register` zurückgab, eine Registrierung mit Update-Ereignissen
// ist (im Browser immer; `Promise<unknown>` im Typ, damit die Attrappen der
// Tests `{}` zurückgeben dürfen).
function isUpdatableRegistration(value: unknown): value is UpdatableRegistration {
  return typeof value === 'object' && value !== null && 'addEventListener' in value && 'waiting' in value
}

/**
 * Ruft `onUpdate` mit dem neuen Worker, sobald eine neue Version installiert
 * ist, während noch eine alte die Seite kontrolliert (docs/DEPLOY.md § 14).
 *
 * Der Worker ruft beim Install kein `skipWaiting` mehr: eine Seite, die mit
 * den alten Bündeln läuft, lädt ihre Chunks weiter aus dem alten Cache, den
 * sein `activate` sonst mitten in der Arbeit wegräumen würde. Übernehmen
 * darf er erst, wenn die Person „Neu laden" drückt (`applyUpdate`). Beim
 * allerersten Install kontrolliert niemand die Seite -- dann gibt es nichts
 * anzukündigen, der Worker aktiviert ohnehin sofort.
 */
export function watchForUpdate(
  registration: UpdatableRegistration,
  container: { controller: unknown | null },
  onUpdate: (worker: WorkerLike) => void,
): void {
  // Schon vor dieser Seite installiert (ein Deploy, zwei Besuche): sofort.
  if (registration.waiting !== null && container.controller !== null) onUpdate(registration.waiting)

  registration.addEventListener('updatefound', () => {
    const installing = registration.installing
    if (installing === null) return

    installing.addEventListener('statechange', () => {
      if (installing.state === 'installed' && container.controller !== null) onUpdate(installing)
    })
  })
}

/**
 * „Neu laden": der wartende Worker übernimmt (`skipWaiting` im Worker), und
 * sobald er die Seite kontrolliert, wird sie neu geladen -- erst dann kommen
 * die neuen Bündel. Nur hier, nie auf ein `controllerchange` von allein: das
 * feuert auch beim ersten Install (`clients.claim`), und ein Reload dort
 * träfe jemanden mitten im ersten Besuch.
 */
export function applyUpdate(
  worker: Pick<WorkerLike, 'postMessage'>,
  container: Pick<ServiceWorkerContainer, 'addEventListener'>,
  reload: () => void,
): void {
  container.addEventListener?.('controllerchange', reload, { once: true })
  worker.postMessage({ type: 'skipWaiting' })
}

/**
 * Löscht die Caches und die Entwürfe -- über den Worker, wenn er die Seite
 * kontrolliert (er läuft weiter, wenn die Seite gleich zur Anmeldeseite
 * navigiert), sonst direkt aus der Seite heraus, so weit sie noch kommt.
 */
export async function clearOfflineData(
  container: ServiceWorkerContainer | undefined,
  cacheStorage: CacheStorageLike | undefined,
  indexedDb: IndexedDbLike | undefined,
): Promise<void> {
  const controller = container?.controller ?? null
  if (controller !== null) {
    controller.postMessage({ type: 'clear' })
    return
  }

  try {
    if (cacheStorage) {
      const keys = await cacheStorage.keys()
      await Promise.all(keys.map((key) => cacheStorage.delete(key)))
    }
    indexedDb?.deleteDatabase(DRAFT_DATABASE)
    // Die Datei, die auf eine Anmeldung wartete, geht mit -- derselbe Grund
    // wie bei den Entwürfen: sie gehört der Person von eben (pendingLaunch.ts).
    indexedDb?.deleteDatabase(PENDING_LAUNCH_DATABASE)
  } catch (error) {
    console.warn('Offline data did not clear', error)
  }
}

/**
 * Räumt beim Anmelden eines *anderen* Kontos, bevor irgendetwas adoptiert
 * oder nachgesendet wird. Wahr, wenn geräumt wurde.
 *
 * Drei Fälle räumen nicht: dieselbe Id (die gewohnte Sitzung), die erste Id
 * überhaupt (nichts Fremdes da), und eine ausgeloggte Seite — deren Entwürfe
 * gehören weiter dem Konto von eben, das sich gleich wieder anmeldet
 * (docs/EDITOR.md, „Offline").
 */
export async function checkAccountChange(
  currentUserId: string | null,
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  clear: () => Promise<void>,
): Promise<boolean> {
  if (currentUserId === null) return false

  const known = storage.getItem(ACCOUNT_STORAGE_KEY)
  const changed = known !== null && known !== currentUserId
  if (changed) await clear()

  storage.setItem(ACCOUNT_STORAGE_KEY, currentUserId)
  return changed
}

/** Der Einstieg aus dem Entrypoint: Meta-Tag lesen, anmelden, Abmelden abfangen. */
export function startServiceWorker(root: Document = document): void {
  const meta = root.querySelector<HTMLMetaElement>(`meta[name="${SERVICE_WORKER_META}"]`)?.content
  const container = typeof navigator === 'undefined' ? undefined : navigator.serviceWorker
  const cacheStorage = typeof caches === 'undefined' ? undefined : caches
  const indexedDb = typeof indexedDB === 'undefined' ? undefined : indexedDB

  // Nach der Registrierung auf eine neue Version horchen: das Band im Layout
  // (ui/updateBanner.ts) sagt es, der Klick lässt den Worker übernehmen.
  void registerServiceWorker(serviceWorkerPath(meta), container, (registration) => {
    if (container === undefined || !isUpdatableRegistration(registration)) return

    watchForUpdate(registration, container, (worker) => {
      showUpdateBanner(root, () => applyUpdate(worker, container, () => window.location.reload()))
    })
  })

  // Caches/IndexedDB und die Push-Anmeldung räumen zusammen -- dieselben
  // zwei Auslöser unten, ein Aufräumauftrag (lib/push.ts#clearPushSubscription
  // dokumentiert, warum Web-Push hier mitgehört: es überlebt sonst die
  // Sitzung).
  const clearAll = (): Promise<void> =>
    Promise.all([clearOfflineData(container, cacheStorage, indexedDb), clearPushSubscription(container)]).then(
      () => undefined,
    )

  // Erst der Kontowechsel-Check, dann darf der Rest der Seite adoptieren:
  // der Editor liest seinen Entwurf asynchron nach dem Mount, das Räumen
  // hier läuft denselben IndexedDB-Weg und kommt ihm zuvor. (Beides ist
  // best effort — ein Browser ohne localStorage räumt eben nicht.)
  try {
    const userId = root.querySelector<HTMLMetaElement>(`meta[name="${ACCOUNT_META}"]`)?.content ?? null
    void checkAccountChange(userId, window.localStorage, clearAll)
  } catch {
    // localStorage kann in exotischen Privatmodi werfen; dann ohne Merker.
  }

  // Capture-Phase, wie die Rückfragen in confirmForm.ts: der Aufräumauftrag
  // geht raus, bevor irgendein anderer Zuhörer die Absendung sieht. Die
  // Navigation wartet nicht darauf -- der Worker erledigt es unabhängig von
  // dieser Seite. Die Sitzung, unter der `clearPushSubscription` seine
  // Löschung noch authentifiziert abschicken kann, ist an dieser Stelle noch
  // gültig -- erst danach übernimmt der Browser die Formularabsendung und
  // navigiert zur Abmeldung.
  root.addEventListener(
    'submit',
    (event) => {
      const form = event.target
      if (!(form instanceof HTMLFormElement) || !isSignOutForm(form)) return

      void clearAll()
      // Der Merker geht mit: die nächste Anmeldung ist ein frischer Start,
      // kein „Wechsel" gegenüber einem Konto, dessen Daten schon weg sind.
      try {
        window.localStorage.removeItem(ACCOUNT_STORAGE_KEY)
      } catch {
        // s. o.
      }
    },
    true,
  )
}
